import { useEffect, useRef, useState } from 'react';
import { useDateNow, useReduxState } from 'hooks';
import {
  getMessageWithPlaceholders,
  localeLanguageCodes
} from 'shared-locales';
import { breakpoints } from 'styles';
import { getFormattedDate } from 'lib/common';
import { Avatar, ProductLinkWithIcon } from 'components/common';
import { Icon } from 'components/common/icons';
import { PathStepProgressStatus } from '.';

import type {
  CourseProgress,
  PathStep,
  PathStepProgress,
  ResolvedItemInPathStep
} from 'shared-types';

interface Props {
  path: {
    id: number;
    deadlines?: { [stepId: string]: string };
    progressData?: PathStepProgress;
    steps: {
      id: PathStep['id'];
      isMandatory: PathStep['isMandatory'];
      items: ResolvedItemInPathStep[];
      number: PathStep['number'];
    }[];
  };
  pathType: 'admin' | 'available' | 'user';
}

const PathsTileSteps = ({ path, pathType }: Props) => {
  const { dateNow } = useDateNow();

  const { language, messages, theme } = useReduxState();

  const scrollableArea = useRef<HTMLDivElement>(undefined);

  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    if (
      scrollableArea.current &&
      scrollableArea.current?.clientHeight <
        scrollableArea.current?.scrollHeight
    )
      setIsScrollable(true);
  }, [scrollableArea.current]);

  const handleScroll = () => {
    if (!scrollableArea.current) return;

    const { clientHeight, scrollHeight, scrollTop } = scrollableArea.current;

    const isAtBottom = scrollHeight - scrollTop <= clientHeight + 1;
    if (scrollTop === 0) {
      scrollableArea.current.classList.remove('inset-above', 'inset-both');
      scrollableArea.current.classList.add('inset-below');
    } else if (isAtBottom) {
      scrollableArea.current.classList.remove('inset-below', 'inset-both');
      scrollableArea.current.classList.add('inset-above');
    } else {
      scrollableArea.current.classList.remove('inset-above', 'inset-below');
      scrollableArea.current.classList.add('inset-both');
    }
  };

  return (
    <div
      className={`steps ${isScrollable ? 'inset-below' : ''}`}
      onScroll={handleScroll}
      ref={scrollableArea}
    >
      {path.steps.map((step) => {
        const deadline = path.deadlines?.[step.id];
        const stepProgress =
          pathType === 'user' ? path.progressData?.steps[step.id] : null;

        return (
          <div className="step" key={step.id}>
            <div className="step-number">
              <Avatar backgroundColor={theme.lightGreyDark} fontSize="1.3rem">
                {step.number}
              </Avatar>
            </div>

            <div className="step-title">
              {step.items?.length > 1 ? (
                <div className="step-alternatives">
                  <div className="step-alternatives-icon">
                    <Icon icon="share-alt" />
                  </div>
                  {getMessageWithPlaceholders({
                    message: messages.commonNumberOfAlternatives,
                    placeholders: { numberOfAlternatives: step.items.length }
                  })}
                </div>
              ) : step.items?.[0] ? (
                <ProductLinkWithIcon
                  disableLink={true}
                  preferredLanguageVersion={
                    step.items[0].type === 'course' && pathType === 'user'
                      ? (
                          stepProgress?.itemProgressByTypeId[
                            `course${step.items[0].id}`
                          ] as CourseProgress
                        )?.completion?.courseLanguageVersion ||
                        localeLanguageCodes[language]
                      : undefined
                  }
                  titleStyle={{
                    display: '-webkit-box',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 1
                  }}
                  {...(step.items[0].type === 'achievement'
                    ? { achievement: step.items[0].achievement }
                    : step.items[0].type === 'course'
                    ? { course: step.items[0].course }
                    : step.items[0].type === 'event'
                    ? { calendarEvent: step.items[0].event }
                    : {})}
                />
              ) : (
                <div className="step-empty">{messages.commonNoContent}</div>
              )}
            </div>

            {deadline && !stepProgress?.completed ? (
              <div
                className="step-deadline"
                aria-label={`${
                  messages.courseTargetSchedule
                }: ${getFormattedDate({ date: deadline })}`}
                data-tooltip={`${
                  messages.courseTargetSchedule
                }: ${getFormattedDate({ date: deadline })}`}
                style={{
                  color:
                    Date.parse(deadline) < dateNow
                      ? theme.dangerColor
                      : theme.textColor
                }}
              >
                <Icon icon={['far', 'clock']} />
              </div>
            ) : null}

            {pathType === 'user' &&
            (stepProgress?.completed || stepProgress?.started) ? (
              <div className="step-progress">
                <PathStepProgressStatus
                  iconOnly={true}
                  id={`pati-tile-step-progress-${step.id}-${path.id}`}
                  pathCompletion={path.progressData?.pathCompletion}
                  progressData={stepProgress}
                />
              </div>
            ) : null}
          </div>
        );
      })}

      <style jsx>{`
        .steps {
          flex-grow: 1;
          max-height: 150px;
          overflow-y: scroll;
        }
        .step {
          align-items: center;
          display: flex;
          gap: 15px;
          padding: 10px 15px;
        }
        .step:not(:first-child) {
          border-top: 1px solid ${theme.lightGrey};
        }
        .step-number {
          flex-grow: 0;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .step-title {
          flex-grow: 1;
          flex-shrink: 1;
        }
        .step-empty {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-style: italic;
        }
        .step-alternatives {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
        }
        .step-alternatives-icon {
          display: inline-block;
          margin-right: 8px;
          text-align: center;
          width: 24px;
        }
        .step-deadline {
          flex-grow: 0;
          flex-shrink: 0;
          font-size: 1.5rem;
          position: relative;
          top: 1px;
        }
        .step-progress {
          flex-grow: 0;
          flex-shrink: 0;
        }
        .inset-above {
          box-shadow: inset 0 8px 10px ${theme.lightGrey};
        }
        .inset-below {
          box-shadow: inset 0 -8px 10px ${theme.lightGrey};
        }
        .inset-both {
          box-shadow: inset 0 -8px 10px ${theme.lightGrey},
            inset 0 8px 10px ${theme.lightGrey};
        }
        @media (min-width: ${breakpoints.small}px) {
          .footer {
            align-items: flex-end;
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};

export default PathsTileSteps;
